import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteWalletDocument,
  getWalletDocumentCategories,
  getWalletDocuments,
  sendWalletDocumentToChat,
  updateWalletDocument,
  uploadWalletDocument,
} from '@shared/api/wallet-documents';
import type { Uuid } from '@shared/types/api';

export const WALLET_DOCUMENTS_QUERY_KEY = ['wallet-documents'];
export const WALLET_CATEGORIES_QUERY_KEY = ['wallet-document-categories'];

export function useWalletDocumentCategories() {
  return useQuery({
    queryKey: WALLET_CATEGORIES_QUERY_KEY,
    queryFn: getWalletDocumentCategories,
    staleTime: 5 * 60_000,
  });
}

export function useWalletDocuments(params?: { category?: string; search?: string }) {
  return useQuery({
    queryKey: [...WALLET_DOCUMENTS_QUERY_KEY, params],
    queryFn: () => getWalletDocuments(params),
    staleTime: 30_000,
  });
}

export function useUploadWalletDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      documentName,
      documentType,
    }: {
      file: File;
      documentName?: string;
      documentType?: string;
    }) => uploadWalletDocument(file, documentName, documentType),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WALLET_DOCUMENTS_QUERY_KEY });
    },
  });
}

export function useUpdateWalletDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      documentName,
      documentType,
      file,
    }: {
      id: Uuid;
      documentName?: string;
      documentType?: string;
      file?: File;
    }) => updateWalletDocument(id, { documentName, documentType, file }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WALLET_DOCUMENTS_QUERY_KEY });
    },
  });
}

export function useDeleteWalletDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => deleteWalletDocument(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WALLET_DOCUMENTS_QUERY_KEY });
    },
  });
}

export function useSendWalletDocumentToChat(transactionId: Uuid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (walletDocId: Uuid) => sendWalletDocumentToChat(walletDocId, transactionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'history', transactionId] });
      qc.invalidateQueries({ queryKey: ['documents', transactionId] });
    },
  });
}
